import { useEffect, useState } from "react";
import { FiPlus, FiEdit2, FiTrash2, FiX } from "react-icons/fi";
import {
  getCategoryList,
  createCategory,
  updateCategory,
  deleteCategory,
  resolveImages,
} from "../../api";
import ImageUploader from "../../components/admin/ImageUploader";
import { useToast } from "../../components/Toast";
import Loader from "../../components/Loader";

const blank = {
  name: "",
  description: "",
  parent: "",
  order: 0,
  active: true,
  image: [],
};

export default function AdminCategories() {
  const [cats, setCats] = useState(null);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast, ToastHost } = useToast();

  const load = () =>
    getCategoryList()
      .then(setCats)
      .catch(() => setCats([]));
  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => {
    setForm(blank);
    setEditId(null);
  };

  const nameById = (id) => cats?.find((c) => c._id === id)?.name || "—";

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast("Name is required", "error");
    setSaving(true);
    try {
      const [image] = await resolveImages(form.image);
      const payload = {
        name: form.name,
        description: form.description,
        parent: form.parent || null,
        order: Number(form.order) || 0,
        active: form.active,
        image:
          image || (typeof form.image[0] === "string" ? form.image[0] : ""),
      };
      if (editId) await updateCategory(editId, payload);
      else await createCategory(payload);
      toast("Category saved");
      reset();
      load();
    } catch (err) {
      toast(err.response?.data?.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const edit = (c) => {
    setEditId(c._id);
    setForm({
      name: c.name,
      description: c.description || "",
      parent: c.parent || "",
      order: c.order || 0,
      active: c.active !== false,
      image: c.image ? [c.image] : [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await deleteCategory(c._id);
      toast("Category deleted");
      load();
    } catch (err) {
      toast(err.response?.data?.message || "Delete failed", "error");
    }
  };

  const toggleActive = async (c) => {
    await updateCategory(c._id, { active: !c.active });
    load();
  };

  if (!cats) return <Loader />;
  // Parent options = all categories except the one being edited.
  const parentOptions = cats.filter((c) => c._id !== editId);

  return (
    <div>
      <div className="acontent__head">
        <div>
          <h1>Categories</h1>
          <p className="muted" style={{ fontSize: 14 }}>
            Build a hierarchy. A category with no children shows its products;
            otherwise it shows sub-categories.
          </p>
        </div>
      </div>

      <form className="acard" onSubmit={save}>
        <div className="between" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>
            {editId ? (
              "Edit category"
            ) : (
              <>
                <FiPlus /> Add category
              </>
            )}
          </h3>
          {editId && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={reset}
            >
              <FiX /> Cancel
            </button>
          )}
        </div>
        <div className="form-grid">
          <div className="field">
            <label>
              Name <span className="req">*</span>
            </label>
            <input
              className="input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Kortex Automation Solutions - Engineering the Future of Manufacturing"
            />
          </div>
          <div className="field">
            <label>Parent category</label>
            <select
              className="select"
              value={form.parent}
              onChange={(e) => set("parent", e.target.value)}
            >
              <option value="">— None (top level) —</option>
              {parentOptions.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field full">
            <label>Description (optional)</label>
            <input
              className="input"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </div>
          <div className="field">
            <label>Order</label>
            <input
              className="input"
              type="number"
              value={form.order}
              onChange={(e) => set("order", e.target.value)}
            />
          </div>
          <div className="field flex center gap">
            <button
              type="button"
              className={`toggle ${form.active ? "on" : ""}`}
              onClick={() => set("active", !form.active)}
            />
            <label style={{ margin: 0 }}>Active</label>
          </div>
          <div className="field full">
            <label>Image</label>
            <ImageUploader
              value={form.image}
              onChange={(v) => set("image", v)}
              multiple={false}
              label="Upload category image"
            />
          </div>
        </div>
        <button className="btn btn-primary" disabled={saving}>
          {saving ? "Saving…" : editId ? "Update category" : "Add category"}
        </button>
      </form>

      <div className="table-wrap">
        <table className="atable">
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Parent</th>
              <th>Order</th>
              <th>Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cats.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="muted"
                  style={{ textAlign: "center", padding: 30 }}
                >
                  No categories yet.
                </td>
              </tr>
            )}
            {cats.map((c) => (
              <tr key={c._id}>
                <td>
                  <img className="thumb" src={c.image || "/logo.png"} alt="" />
                </td>
                <td>
                  {c.name}{" "}
                  {c.hasChildren && (
                    <span className="badge" style={{ marginLeft: 6 }}>
                      parent
                    </span>
                  )}
                </td>
                <td className="muted">{c.parent ? nameById(c.parent) : "—"}</td>
                <td>{c.order}</td>
                <td>
                  <button
                    className={`toggle ${c.active ? "on" : ""}`}
                    onClick={() => toggleActive(c)}
                  />
                </td>
                <td>
                  <div className="row-actions">
                    <button className="icon-btn" onClick={() => edit(c)}>
                      <FiEdit2 />
                    </button>
                    <button
                      className="icon-btn danger"
                      onClick={() => remove(c)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ToastHost />
    </div>
  );
}
